import axios from "axios";
import {url} from "../crudRequests";

// Opening the booking wizard.
//
// One function, used by the patient's own Appointments screen and by a
// guardian booking for a dependent, because the two flows differ in exactly one
// thing: whose appointment it is. Everything else — the session token, the
// clinic app's address, the location in the path — is identical, and a second
// copy would be one more place for the dev/production address rule to drift.
//
// The SUBJECT is not decided here. `patientId` is a request; the server derives
// the real subject from the caller's token and refuses anything the caller has
// no authority over. Passing it at all is a convenience for the staff and
// guardian cases, never a claim.

/**
 * Mint a booking session and return the wizard URL to send the browser to.
 *
 * @param {object} state        app state (carries `jwt`, `db`, `organization`)
 * @param {string} [patientId]  who the booking is for; omit for self
 * @param {string} [locationId] pre-selected site
 */
export async function createBookingWizardUrl(state, {patientId, locationId} = {}) {
  const {data} = await axios.post(
    `${url}/book-now/session`,
    patientId ? {patientId} : {},
    {
      headers: {
        Authorization: "Bearer " + state.jwt,
        // Required, and its absence is not a 401 — it is a 403 "Invalid
        // organization", which reads like a permission problem and is not one.
        //
        // The server derives the tenant from `X-Tenant`, `?db=` or `body.db`,
        // and portal sessions are issued under the literal tenant "patient" so
        // one cookie can span organizations. With no tenant on the request the
        // check compares `undefined` against the token's own db and refuses.
        //
        // This request was the ONE authenticated call in the portal that did
        // not send it. It did not matter while `POST /book-now/session` had no
        // authentication at all; the moment that hole was closed, every
        // "Book an appointment" in the portal — the patient's own and the
        // guardian's — started answering 403.
        "X-Tenant": "patient",
      },
    }
  );

  const path = locationId ? `/onlineBooking/${locationId}` : "/onlineBooking";
  // In development the clinic app runs on :3001; in production it is the
  // organization's own address.
  const isLocal =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const base = isLocal ? "http://localhost:3001" : `https://${state.organization?.url}`;

  return `${base}${path}?token=${data.token}`;
}
